import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { useGSAP } from '@gsap/react'
import { createPortal } from 'react-dom'
import gsap from 'gsap'
import { Check, ChevronDown, LoaderCircle } from 'lucide-react'
import { calculateFloatingMenuPosition } from '../utils/floatingMenu'

gsap.registerPlugin(useGSAP)

const floatingMenuMeasurementStyle: CSSProperties = {
  position: 'fixed',
  right: 'auto',
  bottom: 'auto',
  width: 'max-content',
  minWidth: 0,
  visibility: 'hidden',
}

export interface ParameterOption<T extends string> {
  value: T
  label: string
}

interface ParameterSelectProps<T extends string> {
  value: T
  options: readonly ParameterOption<T>[]
  onChange: (value: T) => void
  ariaLabel: string
  disabled?: boolean
  loading?: boolean
  placement?: 'top' | 'bottom'
  className?: string
  leadingIcon?: ReactNode
  floating?: boolean
}

export function ParameterSelect<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  disabled = false,
  loading = false,
  placement = 'top',
  className = '',
  leadingIcon,
  floating = false,
}: ParameterSelectProps<T>) {
  const [open, setOpen] = useState(false)
  const [pointerClosing, setPointerClosing] = useState(false)
  const [floatingStyle, setFloatingStyle] = useState<CSSProperties | null>(null)
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([])
  const blurFrame = useRef<number | null>(null)
  const pointerSelection = useRef<T | null>(null)
  const pointerCommitted = useRef(false)
  const pointerCloseTimer = useRef<number | null>(null)
  const openInput = useRef<'keyboard' | 'pointer'>('pointer')
  const menuId = useId()
  const selected = options.find((option) => option.value === value) ?? options[0]
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === selected?.value))
  const menuPositioned = !floating || floatingStyle !== null

  const closeMenu = (restoreFocus = false) => {
    if (blurFrame.current !== null) {
      cancelAnimationFrame(blurFrame.current)
      blurFrame.current = null
    }
    if (pointerCloseTimer.current !== null) {
      window.clearTimeout(pointerCloseTimer.current)
      pointerCloseTimer.current = null
    }
    pointerSelection.current = null
    pointerCommitted.current = false
    setPointerClosing(false)
    setOpen(false)
    if (restoreFocus) requestAnimationFrame(() => trigger.current?.focus({ preventScroll: true }))
    else trigger.current?.blur()
  }

  const selectOption = (next: T) => {
    onChange(next)
    closeMenu(true)
  }

  const focusOption = (index: number) => {
    const normalized = (index + options.length) % options.length
    requestAnimationFrame(() => optionRefs.current[normalized]?.focus())
  }

  const openMenu = (index = selectedIndex, input: 'keyboard' | 'pointer' = 'pointer') => {
    if (disabled || loading || !options.length) return
    if (pointerCloseTimer.current !== null) {
      window.clearTimeout(pointerCloseTimer.current)
      pointerCloseTimer.current = null
    }
    pointerSelection.current = null
    pointerCommitted.current = false
    setPointerClosing(false)
    openInput.current = input
    setOpen(true)
    if (input === 'keyboard') focusOption(index)
  }

  useEffect(() => {
    if (!open) return
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node
      if (!root.current?.contains(target) && !menu.current?.contains(target)) setOpen(false)
    }
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  useLayoutEffect(() => {
    if (!open || !floating || !trigger.current || !menu.current) return

    const updatePosition = () => {
      if (!trigger.current || !menu.current) return
      const triggerRect = trigger.current.getBoundingClientRect()
      const visualViewport = window.visualViewport
      const viewport = {
        left: visualViewport?.offsetLeft ?? 0,
        top: visualViewport?.offsetTop ?? 0,
        width: visualViewport?.width ?? document.documentElement.clientWidth,
        height: visualViewport?.height ?? document.documentElement.clientHeight,
      }
      const maxMenuWidth = Math.max(0, Math.min(440, viewport.width - 20))
      const menuWidth = Math.min(Math.max(menu.current.scrollWidth + 2, triggerRect.width), maxMenuWidth)
      const menuHeight = menu.current.scrollHeight + 2
      const position = calculateFloatingMenuPosition({ trigger: triggerRect, menuWidth, menuHeight, viewport, placement })
      setFloatingStyle({
        position: 'fixed',
        right: 'auto',
        bottom: 'auto',
        left: position.left,
        top: position.top,
        width: position.width,
        minWidth: triggerRect.width,
        maxWidth: maxMenuWidth,
        maxHeight: position.maxHeight,
        visibility: 'visible',
      })
    }

    updatePosition()
    const resizeObserver = new ResizeObserver(updatePosition)
    resizeObserver.observe(trigger.current)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    visualViewport?.addEventListener('resize', updatePosition)
    visualViewport?.addEventListener('scroll', updatePosition)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      visualViewport?.removeEventListener('resize', updatePosition)
      visualViewport?.removeEventListener('scroll', updatePosition)
    }
  }, [floating, open, options.length, placement, selected?.label])

  useGSAP(() => {
    const target = menu.current
    if (!open || !menuPositioned || !target) return
    if (openInput.current === 'keyboard' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.set(target, { clearProps: 'opacity,transform,transformOrigin' })
      return
    }
    gsap.fromTo(target, {
      opacity: 0,
      scale: 0.97,
      transformOrigin: placement === 'top' ? 'left bottom' : 'left top',
    }, {
      opacity: 1,
      scale: 1,
      duration: 0.16,
      ease: 'power2.out',
      clearProps: 'opacity,transform,transformOrigin',
    })
  }, { dependencies: [menuPositioned, open, placement], scope: root, revertOnUpdate: true })

  useEffect(() => {
    if (disabled || (loading && pointerSelection.current === null)) setOpen(false)
  }, [disabled, loading])

  useEffect(() => () => {
    if (blurFrame.current !== null) cancelAnimationFrame(blurFrame.current)
    if (pointerCloseTimer.current !== null) window.clearTimeout(pointerCloseTimer.current)
  }, [])

  useEffect(() => {
    if (!open && !pointerClosing) setFloatingStyle(null)
  }, [open, pointerClosing])

  const moveOptionFocus = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      focusOption(index + (event.key === 'ArrowDown' ? 1 : -1))
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault()
      focusOption(event.key === 'Home' ? 0 : options.length - 1)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      trigger.current?.focus()
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      selectOption(options[index].value)
    }
  }

  const menuElement = (open || pointerClosing) && (
    <div
      ref={menu}
      id={menuId}
      className={`parameter-select-menu${pointerClosing ? ' is-pointer-closing' : ''}`}
      role="listbox"
      aria-label={ariaLabel}
      aria-hidden={pointerClosing || undefined}
      style={floating ? (floatingStyle ?? floatingMenuMeasurementStyle) : undefined}
    >
      {options.map((option, index) => (
        <button
          ref={(element) => { optionRefs.current[index] = element }}
          key={option.value}
          type="button"
          role="option"
          aria-selected={option.value === value}
          onKeyDown={(event) => moveOptionFocus(event, index)}
          onPointerDown={(event) => {
            if (event.button !== 0) return
            openInput.current = 'pointer'
            if (pointerCloseTimer.current !== null) window.clearTimeout(pointerCloseTimer.current)
            pointerSelection.current = option.value
            pointerCommitted.current = event.pointerType !== 'touch'
            if (pointerCommitted.current) {
              event.preventDefault()
              onChange(option.value)
            }
          }}
          onPointerUp={(event) => {
            if (!pointerCommitted.current && event.pointerType === 'touch') {
              if (pointerSelection.current !== option.value) return
              setPointerClosing(true)
              setOpen(false)
              onChange(option.value)
              pointerCommitted.current = true
            }
            pointerCloseTimer.current = window.setTimeout(() => {
              if (pointerSelection.current === option.value) closeMenu()
            }, 500)
          }}
          onPointerCancel={() => {
            pointerSelection.current = null
            pointerCommitted.current = false
            setPointerClosing(false)
          }}
          onClick={(event) => {
            if (pointerSelection.current === option.value) closeMenu()
            else if (event.detail === 0) selectOption(option.value)
          }}
        >
          <span>{option.label}</span>
          {option.value === value && <Check aria-hidden="true" />}
        </button>
      ))}
    </div>
  )

  return (
    <div className={`parameter-select parameter-select-${placement}${className ? ` ${className}` : ''}`} ref={root} onBlur={(event) => {
      const relatedTarget = event.relatedTarget as Node | null
      if (relatedTarget && (event.currentTarget.contains(relatedTarget) || menu.current?.contains(relatedTarget))) return
      if (blurFrame.current !== null) cancelAnimationFrame(blurFrame.current)
      blurFrame.current = requestAnimationFrame(() => {
        blurFrame.current = null
        if (pointerSelection.current !== null) return
        if (!root.current?.contains(document.activeElement) && !menu.current?.contains(document.activeElement)) setOpen(false)
      })
    }}>
      <button
        ref={trigger}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-busy={loading}
        disabled={disabled || loading}
        onClick={(event) => {
          const input = event.detail === 0 ? 'keyboard' : 'pointer'
          if (open) closeMenu(input === 'keyboard')
          else openMenu(selectedIndex, input)
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            openMenu(selectedIndex + (event.key === 'ArrowDown' ? 1 : -1), 'keyboard')
          } else if (event.key === 'Escape') {
            setOpen(false)
          }
        }}
      >
        <span className="parameter-select-value">{leadingIcon}<span>{selected?.label ?? value}</span></span>
        {loading ? <LoaderCircle className="parameter-select-spinner" aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
      </button>
      {floating && menuElement ? createPortal(<div className={`parameter-select-portal${className ? ` ${className}` : ''}`}>{menuElement}</div>, document.body) : menuElement}
    </div>
  )
}
